import React, { FC, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { Box, Button, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import { CoreButton, CoreButtonIcon } from '../../../_core/components/_ui/core-button';
import { LanguageEnum, SurveyQuestionInterface } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { AddIcon, DeleteIcon, EditIcon } from '../../../_core/components/icons';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { useMuiModal } from '../../../_core/hooks';
import {
	CreateSurveyTranslation,
	CreateSurveyTranslationProps,
} from '../modal-forms/create-translation-form.component';
import mongoose from 'mongoose';
import {
	useCreateSurveyTemplateMutation,
	useCreateSurveyTemplateTranslationMutation,
	useDeleteSurveyTemplateTranslateMutation,
	useLazyCheckDefaultLanguagesQuery,
	useUpdateSurveyTemplateMutation,
	useUpdateSurveyTemplateTranslateMutation,
} from '../../../../store/api/survey.api';
import { CoreActionButton } from '../../../_core/components/_ui/core-table';
import { mergeSx } from 'merge-sx';
import { ExclamationTooltip } from '../../../_core/components/_ui/exclamation-tooltip';
import { CoreTooltip } from '../../../_core/components/_ui/core-tooltip';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { ProfileRoutes } from '../../../profile/constants';
import { DistrictRoutes } from '../../../district/constants';
import { SurveyTemplateFormRef } from '../../pages/survey-template-actions/survey-template-action.page';

export type SurveySettingsFormProps = {
	currentLanguage: string;
	defaultValues: any;
	allFormsValues: any;
	setAllFormsValues: any;
	setCurrentLanguage: any;
	disableSubmitEnglishTemplate: boolean;
	englishTemplateFormRef: React.RefObject<SurveyTemplateFormRef>;
	translateTemplateFormRef: React.RefObject<SurveyTemplateFormRef>;
};

export type SurveySettingsFormHandle = {
	isDirty: boolean;
};

export const SurveySettingsForm = forwardRef<SurveySettingsFormHandle, SurveySettingsFormProps>((props, ref) => {
	const [t] = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isAdminRoute = location.pathname.startsWith('/admin');
	const [createSurveyTemplate] = useCreateSurveyTemplateMutation();
	const [updateSurveyTemplate] = useUpdateSurveyTemplateMutation();
	const [createSurveyTemplateTranslation] = useCreateSurveyTemplateTranslationMutation();
	const [updateSurveyTemplateTranslation] = useUpdateSurveyTemplateTranslateMutation();
	const [deletedTranslates, setDeletedTranslates] = useState<string[]>([]);
	const [deleteSurveyTemplateTranslation] = useDeleteSurveyTemplateTranslateMutation();
	const [triggerCheckDefaultLanguages] = useLazyCheckDefaultLanguagesQuery();

	const { enqueueSnackbar } = useSnackbar();
	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const {
		defaultValues,
		currentLanguage,
		allFormsValues,
		setAllFormsValues,
		setCurrentLanguage,
		disableSubmitEnglishTemplate,
		englishTemplateFormRef,
		translateTemplateFormRef,
	} = props;

	const languagesList = ['Template'].concat(
		Object.keys(allFormsValues).filter((language: string) => language !== 'Template')
	);
	const methods = useForm({
		defaultValues,
		mode: 'all',
	});

	const {
		register,
		setValue,
		handleSubmit,
		watch,
		getValues,
		reset,
		formState: { errors, isDirty, isLoading, isSubmitting, isSubmitted, isValid },
	} = methods;

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	useImperativeHandle(ref, () => ({
		isDirty,
		isSubmitting,
		isSubmitted,
	}));

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const isDefaultTemplate = watch('isDefaultTemplate');

	const handleSuccessSubmit = async (values: any) => {
		if (values?._id) {
			let updatedSurveyTemplate;
			try {
				updatedSurveyTemplate = await updateSurveyTemplate({
					id: values._id,
					data: {
						...values,
						questions: allFormsValues.Template.questions.map((question: any) =>
							question?.options?.length
								? {
										_id: question._id,
										text: question.text,
										type: question.type,
										isRequired: question.isRequired,
										options: question.options.map((option: any) => ({
											_id: option._id,
											text: option.text,
											isOtherOption: option.isOtherOption,
										})),
								  }
								: {
										_id: question._id,
										text: question.text,
										type: question.type,
										isRequired: question.isRequired,
								  }
						),
					},
				}).unwrap();
				englishTemplateFormRef.current?.resetForm();
			} catch (error) {
				console.log(error);
				showToast('Something goes wrong, try again', 'error');
				return;
			}
			const languageTranslations = Object.keys(allFormsValues).filter((language: string) => language !== 'Template');
			if (updatedSurveyTemplate) {
				for (const languageName of languageTranslations) {
					try {
						const languageValues = allFormsValues[languageName];
						if (languageValues?._id) {
							await updateSurveyTemplateTranslation({
								id: languageValues._id,
								data: {
									_id: languageValues._id,
									mainTemplateId: updatedSurveyTemplate?._id,
									language: languageName,
									isDefault: values.isDefaultTemplate,
									isFinished: languageValues.isFinished,
									questions: languageValues.questions.map((question: any) =>
										question?.options?.length
											? {
													_id: question._id,
													questionId: question.questionId,
													translatedText: question.translatedText,
													options: question.options.map((option: any) => ({
														_id: option._id,
														optionId: option.optionId,
														translatedText: option.translatedText,
													})),
											  }
											: {
													_id: question._id,
													questionId: question.questionId,
													translatedText: question.translatedText,
											  }
									),
								},
							}).unwrap();
							translateTemplateFormRef.current?.resetForm();
						} else {
							await createSurveyTemplateTranslation({
								mainTemplateId: updatedSurveyTemplate?._id,
								language: languageName,
								isDefault: values.isDefaultTemplate,
								isFinished: languageValues.isFinished,
								questions: languageValues.questions.map((question: any) =>
									question?.options?.length
										? {
												_id: question._id,
												questionId: question.questionId,
												translatedText: question.translatedText,
												options: question.options.map((option: any) => ({
													_id: option._id,
													optionId: option.optionId,
													translatedText: option.translatedText,
												})),
										  }
										: {
												_id: question._id,
												questionId: question.questionId,
												translatedText: question.translatedText,
										  }
								),
							}).unwrap();
							translateTemplateFormRef.current?.resetForm();
						}
					} catch (error) {
						console.log(error);
						showToast('Something goes wrong, try again', 'error');
						return;
					}
				}
				for (const deletedTranslateId of deletedTranslates) {
					await deleteSurveyTemplateTranslation({ id: deletedTranslateId }).unwrap();
				}
			}
			showToast('Survey template was successfuly updated', 'success');
		} else {
			let createdSurveyTemplate;
			try {
				createdSurveyTemplate = await createSurveyTemplate({
					isDefaultTemplate: values.isDefaultTemplate,
					adminTemplate: values.adminTemplate,
					title: values.title,
					questions: allFormsValues.Template.questions.map((question: any) =>
						question?.options?.length
							? {
									_id: question._id,
									text: question.text,
									type: question.type,
									isRequired: question.isRequired,
									options: question.options.map((option: any) => ({
										_id: option._id,
										text: option.text,
										isOtherOption: option.isOtherOption,
									})),
							  }
							: {
									_id: question._id,
									text: question.text,
									type: question.type,
									isRequired: question.isRequired,
							  }
					),
					districtIds: values.districtIds,
				}).unwrap();
				englishTemplateFormRef.current?.resetForm();
			} catch (error) {
				console.log(error);
				showToast('Something goes wrong, try again', 'error');
				return;
			}
			const languageTranslations = Object.keys(allFormsValues).filter((language: string) => language !== 'Template');
			if (createdSurveyTemplate) {
				for (const languageName of languageTranslations) {
					try {
						const languageValues = allFormsValues[languageName];
						await createSurveyTemplateTranslation({
							mainTemplateId: createdSurveyTemplate?._id,
							language: languageName,
							isDefault: values.isDefaultTemplate,
							isFinished: languageValues.isFinished,
							questions: languageValues.questions.map((question: any) =>
								question?.options?.length
									? {
											_id: question._id,
											questionId: question.questionId,
											translatedText: question.translatedText,
											options: question.options.map((option: any) => ({
												_id: option._id,
												optionId: option.optionId,
												translatedText: option.translatedText,
											})),
									  }
									: {
											_id: question._id,
											questionId: question.questionId,
											translatedText: question.translatedText,
									  }
							),
						}).unwrap();
						translateTemplateFormRef.current?.resetForm();
					} catch (error) {
						console.log(error);
						showToast('Something goes wrong, try again', 'error');
						return;
					}
				}
			}
			reset(getValues());
			showToast('Survey template was successfuly created', 'success');
		}
		setTimeout(() => {
			if (isAdminRoute) {
				navigate(ProfileRoutes.SurveyTemplates.Root);
			} else {
				navigate(DistrictRoutes.SurveyTemplates.Root);
			}
		}, 10);
	};

	const { openModal: openModalDuplicateDefaultLanguage, closeModal: closeModalDuplicateDefaultLanguage } =
		useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleDefaultLanguageWarning = (duplicateLanguageObject: any) => {
		const languageMap = duplicateLanguageObject as Record<string, string[]>;

		const districtLanguageMap: Record<string, Set<string>> = {};

		for (const [language, districts] of Object.entries(languageMap)) {
			districts.forEach((district) => {
				if (!districtLanguageMap[district]) {
					districtLanguageMap[district] = new Set<string>();
				}
				districtLanguageMap[district].add(language);
			});
		}

		const description = Object.entries(districtLanguageMap)
			.map(([district, languageSet]) => {
				const languages = Array.from(languageSet);
				const languageLine = languages.join(', ');
				return `${district} district: ${languageLine}`;
			})
			.join('\n\n');

		openModalDuplicateDefaultLanguage({
			title: 'Duplicate default survey',
			description: `Some of the languages are already used in other default surveys in:\n\n${description}`,
			hideControls: true,
			modalWidth: 500,
		});
	};

	const { openModal: openModalUnfinishedTranslation, closeModal: closeModalUnfinishedTranslation } =
		useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleUnfinishedTranslationsWarning = (values: any) => {
		openModalUnfinishedTranslation({
			title: 'You have unfinished translations',
			description: `Unfinished translations can not be selected in engagement`,
			onSubmit: async () => {
				await handleSuccessSubmit(values);

				closeModalUnfinishedTranslation();
			},
			onClose: closeModal,
			modalWidth: 600,
			leftButtonProps: {
				children: t('engagements.forms.buttons.cancel'),
				variant: 'secondary',
				sx: buttonModalWindowStyles,
			},
			rightButtonProps: {
				children: t('engagements.forms.buttons.confirm'),
				contraModeStyleButton: true,
				sx: buttonModalWindowStyles,
			},
		});
	};

	const handleFormSubmit = async (values: any) => {
		if (values.districtIds.length && values.isDefaultTemplate) {
			const defaultLanguages = await triggerCheckDefaultLanguages({
				districtIds: values.districtIds,
				languages: Object.keys(allFormsValues),
				currentSurveyId: values?._id,
			}).unwrap();
			if (defaultLanguages && Object.keys(defaultLanguages)?.length) {
				handleDefaultLanguageWarning(defaultLanguages);
				return;
			}
		}
		if (Object.values(allFormsValues).find((el: any) => el.isFinished === false)) {
			handleUnfinishedTranslationsWarning(values);
			return;
		}
		await handleSuccessSubmit(values);
	};

	const { openModal, closeModal } = useMuiModal<CreateSurveyTranslationProps>(CreateSurveyTranslation);
	const handleCreateNewTranslate = (values: any) => {
		setCurrentLanguage(values.language);
		setAllFormsValues((prev: any) => ({
			...prev,
			[values.language]: {
				isTranslation: true,
				isFinished: false,
				questions: prev.Template.questions.map((question: SurveyQuestionInterface) => ({
					...question,
					_id: new mongoose.Types.ObjectId().toString(),
					questionId: question._id,
					translatedText: '',
					options: question.options?.map((option: any) => ({
						_id: new mongoose.Types.ObjectId().toString(),
						optionId: option._id,
						text: option.text,
						translatedText: '',
					})),
				})),
			},
		}));
		closeModal();
	};
	const handleOpenCreateTranslate = () => {
		openModal({
			languageList: Object.values(LanguageEnum),
			submitAction: async (values) => {
				handleCreateNewTranslate(values);
			},
			closeModal,
		});
	};

	const handleDeleteTranslate = (languageName: string) => {
		setAllFormsValues((prev: any) => {
			const copy = { ...prev };
			delete copy[languageName];
			return copy;
		});
		if (currentLanguage === languageName) {
			setCurrentLanguage('Template');
		}
		if (allFormsValues[languageName]?._id) {
			setDeletedTranslates((prev: string[]) => prev.concat(allFormsValues[languageName]?._id));
		}
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit(handleFormSubmit)}
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '20px' }}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography sx={{ fontSize: '24px', fontWeight: 400 }}>{`Survey Settings`}</Typography>
				<CoreButton
					type="submit"
					sx={{ fontSize: '16px' }}
					disabled={disableSubmit || disableSubmitEnglishTemplate}
					loading={loadingSubmit}
				>
					Save changes
				</CoreButton>
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '20px',
					overflowY: 'auto',
				}}
			>
				<CoreTextField
					fullWidth
					label={'Survey Name'}
					placeholder={t('survey-templates.forms.placeholders.enter-survey-name')}
					{...register('title', { required: 'Survey Name is required' })}
					error={!!errors['title']}
					helperText={(errors['title'] as FieldError)?.message}
				/>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<FormControlLabel
						labelPlacement="end"
						sx={{ m: 0 }}
						control={
							<Switch
								checked={
									Object.keys(allFormsValues).filter((language: string) => language !== 'Template').length > 0 &&
									isDefaultTemplate
								}
								disabled={Object.keys(allFormsValues).filter((language: string) => language !== 'Template').length <= 0}
								onChange={(e) => {
									setValue('isDefaultTemplate', e.target.checked, { shouldDirty: true });
								}}
								name="gilad"
								sx={{
									width: 52,
									height: 32,
									padding: 0,
									'& .MuiSwitch-switchBase': {
										padding: 0,
										top: `4px`,
										left: '4px',

										'&.Mui-checked': {
											transform: 'translateX(20px)',
											color: '#fff',
											'& + .MuiSwitch-track': {
												backgroundColor: '#3C8DBC',
												opacity: 1,
												border: 0,
											},
										},
									},
									'& .MuiSwitch-thumb': {
										boxSizing: 'border-box',
										width: 24,
										height: 24,
									},
									'& .MuiSwitch-track': {
										borderRadius: 26 / 2,
										backgroundColor: '#E9E9EA',
										opacity: 1,
									},
								}}
							/>
						}
						label={
							<Box
								component="span"
								sx={{
									color: (theme) => (isDefaultTemplate ? theme.colors.black : theme.colors.gray),
									fontSize: '16px',
									ml: '4px',
								}}
							>
								Make default
							</Box>
						}
					/>
					<ExclamationTooltip
						title={'If set as default, this survey is auto-selected when creating an engagement'}
						iconSx={{ ml: 1 }}
						placement="top-start"
					/>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Translations</Typography>
					<Box>
						{languagesList.map((language: string) => (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: '1px solid lightgrey' }}>
								{allFormsValues[language].isFinished ? (
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											width: '32px',
											height: '32px',
											backgroundColor: 'rgba(54, 171, 85, 0.05)',
											borderRadius: '12px',
											border: language === currentLanguage ? '1px solid #36AB55' : '1px solid white',
										}}
									>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path
												d="M2.75 8.75L6.25 12.25L13.25 4.75"
												stroke="#36AB55"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</Box>
								) : (
									<CoreTooltip
										arrow
										placement="top-start"
										title="This translation is unfinished, this language variant of survey will be unavailable until translation is done"
										variant="white"
									>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												width: '32px',
												height: '32px',
												backgroundColor: 'rgba(236, 157, 29, 0.05)',
												borderRadius: '12px',
												border: language === currentLanguage ? '1px solid #EC9D1D' : '1px solid white',
											}}
										>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path
													d="M1.81656 14C1.69433 14 1.58322 13.9696 1.48322 13.9087C1.38322 13.8478 1.30544 13.7671 1.24989 13.6667C1.19433 13.5662 1.16389 13.4578 1.15856 13.3413C1.15322 13.2249 1.18367 13.1111 1.24989 13L7.41655 2.33333C7.48322 2.22222 7.56944 2.13889 7.67522 2.08333C7.781 2.02778 7.88922 2 7.99989 2C8.11056 2 8.219 2.02778 8.32522 2.08333C8.43144 2.13889 8.51744 2.22222 8.58322 2.33333L14.7499 13C14.8166 13.1111 14.8472 13.2251 14.8419 13.342C14.8366 13.4589 14.8059 13.5671 14.7499 13.6667C14.6939 13.7662 14.6161 13.8469 14.5166 13.9087C14.417 13.9704 14.3059 14.0009 14.1832 14H1.81656ZM2.96655 12.6667H13.0332L7.99989 4L2.96655 12.6667ZM7.99989 12C8.18878 12 8.34722 11.936 8.47522 11.808C8.60322 11.68 8.667 11.5218 8.66655 11.3333C8.66611 11.1449 8.60211 10.9867 8.47456 10.8587C8.347 10.7307 8.18878 10.6667 7.99989 10.6667C7.811 10.6667 7.65278 10.7307 7.52522 10.8587C7.39767 10.9867 7.33367 11.1449 7.33322 11.3333C7.33278 11.5218 7.39678 11.6802 7.52522 11.8087C7.65367 11.9371 7.81189 12.0009 7.99989 12ZM7.99989 10C8.18878 10 8.34722 9.936 8.47522 9.808C8.60322 9.68 8.667 9.52178 8.66655 9.33333V7.33333C8.66655 7.14444 8.60255 6.98622 8.47456 6.85867C8.34655 6.73111 8.18833 6.66711 7.99989 6.66667C7.81144 6.66622 7.65322 6.73022 7.52522 6.85867C7.39722 6.98711 7.33322 7.14533 7.33322 7.33333V9.33333C7.33322 9.52222 7.39722 9.68067 7.52522 9.80867C7.65322 9.93667 7.81144 10.0004 7.99989 10Z"
													fill="#EC9D1D"
												/>
											</svg>
										</Box>
									</CoreTooltip>
								)}

								<Typography>{language}</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
									<Tooltip title={language !== 'Template' ? 'Edit Translation Template' : 'Edit Template'}>
										<CoreActionButton
											variant="secondary"
											sx={mergeSx(
												{ width: 32, height: 32, borderColor: (theme) => theme.colors.lightBlue, borderWidth: '1px' },
												currentLanguage === language
													? { color: (theme) => theme.colors.white, backgroundColor: (theme) => theme.colors.lightBlue }
													: null
											)}
											onClick={() => {
												setCurrentLanguage(language);
											}}
										>
											<EditIcon sx={{ fontSize: '24px' }} />
										</CoreActionButton>
									</Tooltip>
									{language !== 'Template' ? (
										<Tooltip title={'Delete Translation Template'}>
											<CoreActionButton
												variant="secondary"
												sx={{
													width: 32,
													height: 32,
													borderWidth: '1px',
													borderColor: (theme) => theme.colors.orange,
													color: (theme) => theme.colors.orange,
													'&:hover, &.Mui-focusVisible': {
														borderColor: (theme) => theme.colors.orangeDeep,
													},
												}}
												onClick={() => {
													handleDeleteTranslate(language);
												}}
											>
												<DeleteIcon sx={{ fontSize: '24px' }} />
											</CoreActionButton>
										</Tooltip>
									) : null}
								</Box>
							</Box>
						))}
					</Box>
					<Button
						variant="text"
						onClick={handleOpenCreateTranslate}
						sx={{
							py: '4px',
							px: '12px',
							display: 'flex',
							gap: '12px',
							alignItems: 'center',
							justifyContent: 'flex-start',
							width: 'fit-content',
							minWidth: 'unset',
						}}
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M2 8H14" stroke="#3C8DBC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
							<path d="M8 2V14" stroke="#3C8DBC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
						</svg>

						<Typography
							sx={{
								color: (theme) => theme.colors.lightBlue,
								textAlign: 'center',
								fontSize: '16px',
								fontWeight: 500,
								textTransform: 'none',
							}}
						>
							Create Translation
						</Typography>
					</Button>
				</Box>
			</Box>
		</Box>
	);
});
